import React, { useEffect, useMemo, useState } from 'react';
import { manifestTemplatesAPI } from '../../services/api';
import { ALL_FIELD_KEYS, FIELD_GROUPS } from '../../utils/manifest/manifestTemplateFields';
import Table from '../Common/Table';
import Modal from '../Common/Modal';
import ManifestPrintPreview from './ManifestPrintPreview';

function uniq(arr) {
  const seen = new Set();
  const out = [];
  for (const v of arr || []) {
    const s = String(v);
    if (seen.has(s)) continue;
    seen.add(s);
    out.push(s);
  }
  return out;
}

const ManifestTemplatesManagement = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    is_active: true,
  });
  const [selectedFields, setSelectedFields] = useState([]);

  const allFieldKeys = useMemo(() => ALL_FIELD_KEYS, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await manifestTemplatesAPI.getAll();
      setTemplates(Array.isArray(data) ? data : []);
    } catch (e) {
      alert('Error loading templates: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const resetForm = () => {
    setFormData({ name: '', is_active: true });
    setSelectedFields([]);
  };

  const openPreview = (name, fields) => {
    setPreviewTemplate({
      name: name || 'Manifest',
      fields: uniq(fields),
    });
  };

  const handleAdd = () => {
    setEditingTemplate(null);
    resetForm();
    setShowModal(true);
  };

  const handleEdit = (row) => {
    setEditingTemplate(row);
    setFormData({ name: row.name || '', is_active: row.is_active !== false });
    setSelectedFields(Array.isArray(row.fields) ? row.fields.map(String) : []);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await manifestTemplatesAPI.delete(id);
      loadData();
    } catch (e) {
      alert('Error deleting template: ' + e.message);
    }
  };

  const toggleField = (key) => {
    setSelectedFields((prev) => {
      const s = String(key);
      const set = new Set(prev.map(String));
      if (set.has(s)) set.delete(s);
      else set.add(s);
      return Array.from(set);
    });
  };

  const setAll = (on) => {
    if (on) setSelectedFields(uniq(allFieldKeys));
    else setSelectedFields([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: String(formData.name || '').trim(),
        is_active: !!formData.is_active,
        fields: uniq(selectedFields),
      };
      if (!payload.name) throw new Error('Template name is required.');
      if (!payload.fields.length) throw new Error('Select at least 1 field.');

      if (editingTemplate) {
        await manifestTemplatesAPI.update(editingTemplate.id, payload);
      } else {
        await manifestTemplatesAPI.create(payload);
      }
      setShowModal(false);
      setEditingTemplate(null);
      resetForm();
      loadData();
    } catch (e2) {
      alert('Error saving template: ' + e2.message);
    }
  };

  const columns = [
    { key: 'name', label: 'Template Name' },
    { key: 'is_active', label: 'Active', format: (v) => (v ? 'Yes' : 'No') },
    { key: 'fields', label: 'Fields', format: (v) => (Array.isArray(v) ? `${v.length}` : '0') },
  ];

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Manifest Template Management</h1>
        <button className="btn-primary" type="button" onClick={handleAdd}>
          Add New Template
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <Table
          data={templates}
          columns={columns}
          onEdit={handleEdit}
          onDelete={(rowId) => handleDelete(rowId)}
          renderActions={(row) => (
            <button
              type="button"
              className="btn-secondary"
              style={{ marginRight: 6 }}
              onClick={() => openPreview(row.name, row.fields)}
            >
              Preview
            </button>
          )}
        />
      )}

      {previewTemplate ? (
        <ManifestPrintPreview
          templateName={previewTemplate.name}
          fields={previewTemplate.fields}
          onClose={() => setPreviewTemplate(null)}
        />
      ) : null}

      <Modal
        show={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingTemplate(null);
          resetForm();
        }}
        title={editingTemplate ? 'Edit Manifest Template' : 'Add Manifest Template'}
        size="xl"
      >
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Template Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
              required
            />
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={!!formData.is_active}
                onChange={(e) => setFormData((p) => ({ ...p, is_active: e.target.checked }))}
              />
              Active
            </label>
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center', margin: '10px 0', flexWrap: 'wrap' }}>
            <strong>Select fields to include</strong>
            <button type="button" className="btn-secondary" onClick={() => setAll(true)}>
              Select all
            </button>
            <button type="button" className="btn-secondary" onClick={() => setAll(false)}>
              Clear
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => openPreview(formData.name || 'Untitled template', selectedFields)}
            >
              Preview
            </button>
            <span style={{ color: '#666' }}>{selectedFields.length} selected</span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {FIELD_GROUPS.map((g) => (
              <div key={g.group} style={{ border: '1px solid #eee', borderRadius: 8, padding: 10 }}>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>{g.group}</div>
                <div style={{ display: 'grid', gap: 6 }}>
                  {g.fields.map((f) => {
                    const checked = selectedFields.includes(f.key);
                    return (
                      <label key={f.key} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input type="checkbox" checked={checked} onChange={() => toggleField(f.key)} />
                        {f.label}
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="form-actions" style={{ marginTop: 16 }}>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setShowModal(false);
                setEditingTemplate(null);
                resetForm();
              }}
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              Save
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ManifestTemplatesManagement;
